import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmTabState } from 'common/hooks/usePalmTabState';
import { fetchUserClaimMemberships } from 'common/services/api/UserClaimMembership/UserClaimMembership.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';

import { UserClaimMembershipCreateContent } from './UserClaimMembershipCreateDrawer';
import { UserClaimMembershipDeleteDialogContent } from './UserClaimMembershipDeleteDialogContent';
import { addNewFilter, clearFilters, removeFilter, selectFilters } from './userClaimMembershipSlice';

export function UserClaimMembershipTab() {
    const typeName = 'Claim';
    const { suiteId = '', tenantId ='', userId = '' } = useParams();

    const shouldDisableCreateButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager, Roles.AppManager, Roles.MultiTenantRO]);
    const shouldDisableEditButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const shouldDisableDeleteButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);

    const { processing, sortModel, setSortModel, filterProp, fetchEntities } = usePalmTabState({
        typeName,
        fetchParams: { tenantId, userId },
        fetchThunk: fetchUserClaimMemberships,
        filterActions: { selectFilters, addNewFilter, removeFilter, clearFilters },
        sortField: 'claimName'
    });

    const onGetRowLink = useCallback((claimId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Claims}/${claimId}/${Routes.Users}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableEditButton },
        { action: Operations.Delete, disabled: shouldDisableDeleteButton },
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'claimId', columnName: 'Claim ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'claimName', columnName: 'Claim Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'claimDataTypeName', columnName: 'Claim Type', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'value', columnName: 'Claim Value', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        title={typeName}
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        operationName={Operations.Link}
        filter={filterProp}
        contentFunction={UserClaimMembershipCreateContent}
        shouldShowCreateButton={!shouldDisableCreateButton}
        deleteFunction={UserClaimMembershipDeleteDialogContent}
        customDeleteTitle={'Are you sure you want to remove this User from this Claim?'}
        refreshDataGridClick={() => fetchEntities()}
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
